import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

//App
import { UserProps } from "../../../types";
import { UsersContext } from "../UsersContainer";
import useForm from "../../hooks/useForm";
import {
  UnlockUserPayload,
  UserContextData,
  UserResendAccountConfirmationPayload,
  UserResetPasswordPayload,
} from "../../../types/user";
import { queryKeys, USER_STATUS_LOOKUP_TYPE_ID } from "../../Users/constants";
import userClaimApi from "../../../app/model/api/userClaimApi";
import userApi from "../../../app/model/api/userApi";
import { EMPTY_GUID } from "../../../utility/constants";
import lookupValueApi from "../../../app/model/api/lookupValueApi";
import { SearchValue } from "../../../store/types";

const useUser = (props: UserProps) => {
  return useForm<UserContextData, UserProps>(UsersContext, props);
};

export const useUserPermissionLookupQuery = (userId: string | null) =>
  useQuery({
    queryKey: [queryKeys.getRoleClaims],
    queryFn: () => userClaimApi.getLookups(userId!),
    enabled: userId !== null,
  });

export const useUserPermissionsQuery = (userId: string | null) =>
  useQuery({
    queryKey: [queryKeys.getUserClaims, userId],
    queryFn: () => userClaimApi.get(userId!),
    enabled: userId !== null,
  });

export const useUserQuery = (userId: string | null) =>
  useQuery({
    queryKey: [queryKeys.getUser, userId],
    queryFn: () => userApi.get(userId ?? EMPTY_GUID),
    enabled: userId !== null,
  });

export const useUsersGridQuery = (
  searchPayload: SearchValue | null,
  statusId: string | null | undefined
) =>
  useQuery({
    queryKey: [queryKeys.getAllUsers, searchPayload, statusId],
    queryFn: () =>
      userApi.getAll({
        ...searchPayload,
        statusId: statusId ?? undefined,
      }),
    placeholderData: keepPreviousData,
    enabled: statusId !== undefined,
  });

export const useUserStatusLookupQuery = () =>
  useQuery({
    queryKey: [queryKeys.getUserLookups, USER_STATUS_LOOKUP_TYPE_ID],
    queryFn: () =>
      lookupValueApi.getAllByLookupTypeId({
        lookupTypeId: USER_STATUS_LOOKUP_TYPE_ID,
        isActive: true,
      }),
  });

export const useUserLookupQuery = (userId?: string | null) =>
  useQuery({
    queryKey: [queryKeys.getUserLookups],
    queryFn: () => userApi.getLookups(),
    enabled: userId !== null,
  });
export const useDeleteMutation = () =>
  useMutation<void, Error, string>({
    mutationFn: async (userId: string) => {
      await userApi.delete(userId);
    },
  });

export const useActivateMutation = () =>
  useMutation<void, Error, string>({
    mutationFn: async (userId: string) => {
      await userApi.activate(userId);
    },
  });

export const useResendAccountConfirmationMutation = () =>
  useMutation<boolean, Error, UserResendAccountConfirmationPayload>({
    mutationFn: async (payload: UserResendAccountConfirmationPayload) => {
      const response = await userApi.resendConfirmationEmail(payload);
      return response.result;
    },
  });

export const useUnlockUserMutation = () =>
  useMutation<void, Error, UnlockUserPayload>({
    mutationFn: async (payload: UnlockUserPayload) => {
      await userApi.unlockUser(payload);
    },
  });

export const useResetPasswordMutation = () =>
  useMutation<void, Error, UserResetPasswordPayload>({
    mutationFn: async (payload: UserResetPasswordPayload) => {
      const response = await userApi.resetPassword(payload);
      return response.result;
    },
  });

export default useUser;
